import {
  ZadCustomer,
  ZadDiscountType,
  ZadProduct,
  ZadSale,
  ZadSaleItemWritePayload,
  ZadSaleStatus,
  ZadSaleWritePayload,
  ZadSaleUnit,
  ZadStockOverviewItem,
} from '../../../models/zad'
import {getZadCustomerDisplayName} from './customerHelpers'
import {resolveZadUserMessage, ZadApiError} from './errors'
import {parseZadPriceInput, preserveZadBarcode, roundZadMoney3} from './productHelpers'
import {normalizeZadSalePromoCodeInput} from './salePromotionHelpers'
import {parseZadStockQuantity} from './stockHelpers'
import {
  defaultZadSaleUnit,
  isZadPackagingEnabled,
  normalizeZadSaleUnit,
  resolveZadSaleUnitPrice,
  saleLineKey,
  toZadBaseUnits,
  zadPackagingLabel,
  zadUnitsPerPackage,
} from './packagingHelpers'

export const ZAD_SALE_STATUS_I18N: Record<ZadSaleStatus, string> = {
  DRAFT: 'ZAD.SALES.STATUS_DRAFT',
  VALIDATED: 'ZAD.SALES.STATUS_VALIDATED',
  CANCELLED: 'ZAD.SALES.STATUS_CANCELLED',
}

export const ZAD_SALE_DISCOUNT_I18N: Record<ZadDiscountType, string> = {
  NONE: 'ZAD.SALES.DISCOUNT_NONE',
  PERCENT: 'ZAD.SALES.DISCOUNT_PERCENT',
  FIXED: 'ZAD.SALES.DISCOUNT_FIXED',
}

export type ZadSaleEditorItem = {
  key: string
  product: string
  productName: string
  productCode: string
  barcode: string
  quantity: string
  unitPrice: string
  discountType: ZadDiscountType
  discountValue: string
  saleUnit?: ZadSaleUnit
  unitsPerPackageSnapshot?: number
  packagingLabelSnapshot?: string | null
  packagingEnabled?: boolean
}

export type ZadSaleEditorValues = {
  customer: string
  walkInCustomerName: string
  saleDate: string
  notes: string
  globalDiscountType: ZadDiscountType
  globalDiscountValue: string
  promoCodeInput: string
  items: ZadSaleEditorItem[]
}

export type ZadSaleItemPreview = {
  quantity: number
  unitPrice: number
  discountType: ZadDiscountType
  discountValue: number
  discountAmount: number
  netUnitPrice: number
  lineSubtotal: number
  lineDiscountAmount: number
  lineTotal: number
}

export type ZadSaleTotalsPreview = {
  items: ZadSaleItemPreview[]
  subtotal: number
  itemDiscountTotal: number
  afterItemDiscounts: number
  globalDiscountType: ZadDiscountType
  globalDiscountValue: number
  globalDiscountAmount: number
  totalDiscount: number
  total: number
}

export function canEditZadSale(status?: ZadSaleStatus | string): boolean {
  return status === 'DRAFT'
}

export function canValidateZadSale(status?: ZadSaleStatus | string): boolean {
  return status === 'DRAFT'
}

export function canCancelZadSale(status?: ZadSaleStatus | string): boolean {
  return status === 'DRAFT'
}

export function canPrintZadSaleTicket(status?: ZadSaleStatus | string): boolean {
  return status === 'VALIDATED'
}

export const ZAD_WALK_IN_CUSTOMER_NAME_MAX = 120

export type ZadSaleTicketCustomer = {
  isWalkIn: boolean
  displayName: string
  customerNumber?: string
  walkInCustomerName?: string
}

export type ZadPrintableCustomerKind = 'WALK_IN' | 'REGISTERED'

export type ZadPrintableCustomerIdentity = {
  name: string | null
  kind: ZadPrintableCustomerKind
  showWalkInSecondary: boolean
  customerNumber?: string
}

type ZadPrintableCustomerSource = {
  customer?: ZadSale['customer'] | ZadCustomer | string | null
  customerSnapshot?: ZadSale['customerSnapshot']
  customerKind?: string | null
  walkInCustomerName?: string | null
  customerName?: string | null
  walkIn?: boolean
  context?: {customerName?: string | null} | null
  allocations?: Array<{sale?: unknown}>
}

function trimPrintableName(value?: string | null): string {
  return String(value || '').trim()
}

function walkInNameFromAllocatedSale(sale: unknown): string {
  if (!sale || typeof sale !== 'object') return ''
  const row = sale as {walkInCustomerName?: string | null; customerName?: string | null}
  return trimPrintableName(row.walkInCustomerName) || trimPrintableName(row.customerName)
}

export function getZadPrintableCustomerIdentity(
  source?: ZadPrintableCustomerSource | null
): ZadPrintableCustomerIdentity {
  if (!source) return {name: null, kind: 'WALK_IN', showWalkInSecondary: false}
  const kindRaw = String(source.customerKind || '').toUpperCase()
  const hasCustomer = Boolean(getZadSaleCustomerId(source.customer))
  const snapshotName = trimPrintableName(source.customerSnapshot?.displayName)
  const snapshotNumber = trimPrintableName(source.customerSnapshot?.customerNumber)
  const hasRegisteredSnapshot = Boolean(snapshotName || snapshotNumber)
  const isWalkIn =
    source.walkIn === true ||
    kindRaw === 'WALK_IN' ||
    (kindRaw !== 'REGISTERED' && !hasCustomer && !hasRegisteredSnapshot)
  if (isWalkIn) {
    const fromAllocation = (source.allocations || [])
      .map((row) => walkInNameFromAllocatedSale(row?.sale))
      .find(Boolean)
    const name =
      trimPrintableName(source.walkInCustomerName) ||
      trimPrintableName(source.customerName) ||
      trimPrintableName(source.context?.customerName) ||
      fromAllocation ||
      null
    return {name, kind: 'WALK_IN', showWalkInSecondary: Boolean(name)}
  }
  const liveName =
    source.customer && typeof source.customer !== 'string'
      ? getZadCustomerDisplayName(source.customer)
      : ''
  const name = snapshotName || liveName || trimPrintableName(source.customerName) || null
  return {
    name,
    kind: 'REGISTERED',
    showWalkInSecondary: false,
    customerNumber: snapshotNumber || undefined,
  }
}

export function getZadSaleIdentity(
  sale?: Pick<
    ZadSale,
    'customer' | 'customerSnapshot' | 'customerKind' | 'walkInCustomerName' | 'customerName'
  > | null
): {
  isWalkIn: boolean
  walkInCustomerName: string
  registeredName: string
  customerNumber?: string
} {
  const printed = getZadPrintableCustomerIdentity(sale)
  const isWalkIn = printed.kind === 'WALK_IN'
  return {
    isWalkIn,
    walkInCustomerName: isWalkIn ? printed.name || '' : '',
    registeredName: isWalkIn ? '' : printed.name || '',
    customerNumber: printed.customerNumber,
  }
}

export function getZadSaleTicketCustomer(
  sale?: Pick<
    ZadSale,
    'customer' | 'customerSnapshot' | 'customerKind' | 'walkInCustomerName' | 'customerName'
  > | null
): ZadSaleTicketCustomer {
  const identity = getZadPrintableCustomerIdentity(sale)
  if (identity.kind === 'WALK_IN') {
    return {
      isWalkIn: true,
      displayName: identity.name || '',
      walkInCustomerName: identity.name || undefined,
    }
  }
  return {
    isWalkIn: false,
    displayName: identity.name || '',
    customerNumber: identity.customerNumber,
  }
}

export function canShowZadSaleCostMargin(role?: string | number | null): boolean {
  return String(role ?? '') === '2'
}

export function isZadSaleQtyOverStock(
  quantity: string | number,
  available: number | undefined,
  conversion?: {saleUnit?: string | null; unitsPerPackage?: number | null}
): boolean {
  if (available == null) return false
  const qty = parseZadStockQuantity(quantity)
  if (!qty.ok) return false
  const base = toZadBaseUnits(qty.value, conversion?.saleUnit, conversion?.unitsPerPackage)
  return base > available
}

export function zadSaleItemBaseUnits(
  item: Pick<ZadSaleEditorItem, 'quantity' | 'saleUnit' | 'unitsPerPackageSnapshot'>
): number {
  const qty = parseZadStockQuantity(item.quantity)
  if (!qty.ok) return 0
  return toZadBaseUnits(qty.value, item.saleUnit, item.unitsPerPackageSnapshot)
}

export function isZadSaleProductOverStock(
  items: ZadSaleEditorItem[],
  productId: string,
  available: number | undefined
): boolean {
  if (available == null) return false
  const demand = items
    .filter((item) => item.product === productId)
    .reduce((sum, item) => sum + zadSaleItemBaseUnits(item), 0)
  return demand > available
}

export function getZadSaleCustomerId(
  customer: ZadSale['customer'] | string | null | undefined
): string {
  if (!customer) return ''
  if (typeof customer === 'string') return customer
  return customer._id || ''
}

export function getZadSaleCustomerLabel(
  sale?: Pick<
    ZadSale,
    'customer' | 'customerSnapshot' | 'customerKind' | 'walkInCustomerName' | 'customerName'
  > | null
): string {
  if (!sale) return ''
  const identity = getZadSaleIdentity(sale)
  if (identity.isWalkIn) return identity.walkInCustomerName
  return identity.registeredName
}

export function getZadSaleWalkInLabelId(): string {
  return 'ZAD.SALES.WALK_IN'
}

export function getZadSaleProductId(product: ZadSale['items'][number]['product']): string {
  if (!product) return ''
  if (typeof product === 'string') return product
  return product._id || ''
}

export function normalizeZadScannerBarcode(value: unknown): string {
  return preserveZadBarcode(value).trim()
}

export function findZadProductByExactBarcode(
  products: ZadProduct[],
  barcode: string
): ZadProduct | null {
  const term = normalizeZadScannerBarcode(barcode)
  if (!term) return null
  const matches = products.filter((product) => normalizeZadScannerBarcode(product.barcode) === term)
  return matches.length === 1 ? matches[0] : null
}

/** Display hint only. Does not change stock quantities or validation. */
export const ZAD_SALE_LOW_STOCK_HINT = 5

export function describeZadSaleStock(
  stock: number | null | undefined
): 'unknown' | 'out' | 'low' | 'ok' {
  if (stock == null || !Number.isFinite(Number(stock))) return 'unknown'
  const value = Number(stock)
  if (value <= 0) return 'out'
  if (value <= ZAD_SALE_LOW_STOCK_HINT) return 'low'
  return 'ok'
}

export function pickZadSaleCatalogProduct(
  products: ZadProduct[],
  query: string
): ZadProduct | null {
  const active = products.filter((product) => product.isActive !== false)
  const barcode = findZadProductByExactBarcode(active, query)
  if (barcode) return barcode
  const term = query.trim().toLowerCase()
  if (!term) return null
  const byCode = active.filter((product) => String(product.code || '').trim().toLowerCase() === term)
  if (byCode.length === 1) return byCode[0]
  if (active.length === 1) return active[0]
  return null
}

export function bumpZadSaleQuantity(current: string, delta: number): string {
  const parsed = parseZadStockQuantity(current)
  const next = (parsed.ok ? parsed.value : 1) + delta
  return String(Math.max(1, next))
}

export function addOrIncrementZadSaleProduct(
  items: ZadSaleEditorItem[],
  product: ZadProduct,
  saleUnit?: string | null
): {items: ZadSaleEditorItem[]; incremented: boolean} {
  const unit = defaultZadSaleUnit(product, saleUnit)
  const existing = items.find(
    (item) => saleLineKey(item.product, item.saleUnit) === saleLineKey(product._id, unit)
  )
  if (existing) {
    return {
      incremented: true,
      items: items.map((item) =>
        saleLineKey(item.product, item.saleUnit) === saleLineKey(product._id, unit)
          ? {...item, quantity: bumpZadSaleQuantity(item.quantity, 1)}
          : item
      ),
    }
  }
  return {
    incremented: false,
    items: [...items, createZadSaleEditorItem(product, unit)],
  }
}

export function previewZadSaleItem(values: {
  quantity: string | number
  unitPrice: string | number
  discountType: ZadDiscountType
  discountValue: string | number
}): ZadSaleItemPreview | null {
  const qty = parseZadStockQuantity(values.quantity)
  const price = parseZadPriceInput(values.unitPrice, {required: true})
  if (!qty.ok || !price.ok) return null
  const type = values.discountType || 'NONE'
  let discountValue = 0
  if (type !== 'NONE') {
    const parsed = parseZadPriceInput(values.discountValue, {required: false, defaultValue: 0})
    if (!parsed.ok) return null
    discountValue = parsed.value
  }
  const unit = roundZadMoney3(price.value)
  let discountAmount = 0
  if (type === 'PERCENT') {
    if (discountValue > 100) return null
    discountAmount = roundZadMoney3((unit * discountValue) / 100)
  } else if (type === 'FIXED') {
    if (discountValue > unit) return null
    discountAmount = roundZadMoney3(discountValue)
  }
  const netUnitPrice = roundZadMoney3(unit - discountAmount)
  return {
    quantity: qty.value,
    unitPrice: unit,
    discountType: type,
    discountValue: type === 'NONE' ? 0 : discountValue,
    discountAmount,
    netUnitPrice,
    lineSubtotal: roundZadMoney3(qty.value * unit),
    lineDiscountAmount: roundZadMoney3(qty.value * discountAmount),
    lineTotal: roundZadMoney3(qty.value * netUnitPrice),
  }
}

export function previewZadSaleTotals(
  items: Array<{
    quantity: string | number
    unitPrice: string | number
    discountType: ZadDiscountType
    discountValue: string | number
  }>,
  globalDiscountType: ZadDiscountType,
  globalDiscountValue: string | number
): ZadSaleTotalsPreview | null {
  const previews = items.map((item) => previewZadSaleItem(item))
  if (previews.some((item) => !item) || previews.length === 0) return null
  const calculated = previews as ZadSaleItemPreview[]
  const subtotal = roundZadMoney3(calculated.reduce((sum, item) => sum + item.lineSubtotal, 0))
  const itemDiscountTotal = roundZadMoney3(
    calculated.reduce((sum, item) => sum + item.lineDiscountAmount, 0)
  )
  const afterItemDiscounts = roundZadMoney3(subtotal - itemDiscountTotal)
  const gType = globalDiscountType || 'NONE'
  let gValue = 0
  if (gType !== 'NONE') {
    const parsed = parseZadPriceInput(globalDiscountValue, {required: false, defaultValue: 0})
    if (!parsed.ok) return null
    gValue = parsed.value
  }
  let globalDiscountAmount = 0
  if (gType === 'PERCENT') {
    if (gValue > 100) return null
    globalDiscountAmount = roundZadMoney3((afterItemDiscounts * gValue) / 100)
  } else if (gType === 'FIXED') {
    if (gValue > afterItemDiscounts) return null
    globalDiscountAmount = roundZadMoney3(gValue)
  }
  const total = roundZadMoney3(afterItemDiscounts - globalDiscountAmount)
  return {
    items: calculated,
    subtotal,
    itemDiscountTotal,
    afterItemDiscounts,
    globalDiscountType: gType,
    globalDiscountValue: gType === 'NONE' ? 0 : gValue,
    globalDiscountAmount,
    totalDiscount: roundZadMoney3(itemDiscountTotal + globalDiscountAmount),
    total,
  }
}

function todayIso(): string {
  const today = new Date()
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(
    today.getDate()
  ).padStart(2, '0')}`
}

const WALK_IN_CUSTOMER_TOKENS = new Set(['', 'WALK_IN', 'WALK-IN', 'NULL', 'NONE', 'undefined'])

/** Client comptoir is stored as '' in the editor — never a truthy placeholder. */
export function isZadRegisteredCustomerRef(customer?: string | null): boolean {
  const raw = String(customer ?? '').trim()
  if (!raw) return false
  return !WALK_IN_CUSTOMER_TOKENS.has(raw.toUpperCase())
}

export function emptyZadSaleEditorValues(): ZadSaleEditorValues {
  return {
    customer: '',
    walkInCustomerName: '',
    saleDate: todayIso(),
    notes: '',
    globalDiscountType: 'NONE',
    globalDiscountValue: '0',
    promoCodeInput: '',
    items: [],
  }
}

export function toZadSaleEditorValues(sale?: ZadSale | null): ZadSaleEditorValues {
  if (!sale) return emptyZadSaleEditorValues()
  const date = sale.saleDate ? new Date(sale.saleDate) : new Date()
  const iso = Number.isNaN(date.getTime())
    ? todayIso()
    : `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
        date.getDate()
      ).padStart(2, '0')}`
  const registeredId = getZadSaleCustomerId(sale.customer)
  const identity = getZadSaleIdentity(sale)
  return {
    customer: registeredId,
    walkInCustomerName: registeredId
      ? ''
      : identity.walkInCustomerName || trimPrintableName(sale.walkInCustomerName),
    saleDate: iso,
    notes: sale.notes || '',
    globalDiscountType: sale.globalDiscountType || 'NONE',
    globalDiscountValue: String(sale.globalDiscountValue ?? 0),
    promoCodeInput: normalizeZadSalePromoCodeInput(sale.promoCodeInput),
    items: (sale.items || []).map((item, index) => ({
      key: item._id || `item-${index}`,
      product: getZadSaleProductId(item.product),
      productName: item.productName || '',
      productCode: item.productCode || '',
      barcode: item.barcode || '',
      quantity: String(item.quantity ?? ''),
      unitPrice: String(item.unitPrice ?? ''),
      discountType: item.discountType || 'NONE',
      discountValue: String(item.discountValue ?? 0),
      saleUnit: normalizeZadSaleUnit(item.saleUnit),
      unitsPerPackageSnapshot:
        item.unitsPerPackageSnapshot != null ? Number(item.unitsPerPackageSnapshot) : undefined,
      packagingLabelSnapshot: item.packagingLabelSnapshot || null,
      packagingEnabled:
        item.unitsPerPackageSnapshot != null ||
        String(item.saleUnit || '').toUpperCase() === 'PACKAGE' ||
        Boolean(item.packagingLabelSnapshot),
    })),
  }
}

export function validateZadSaleForm(values: ZadSaleEditorValues): string | null {
  if (!String(values.saleDate || '').trim()) return 'ZAD.SALES.DATE_REQUIRED'
  if (!String(values.customer || '').trim()) {
    const walkInName = String(values.walkInCustomerName || '').trim()
    if (walkInName.length > ZAD_WALK_IN_CUSTOMER_NAME_MAX) return 'ZAD.SALES.WALK_IN_NAME_TOO_LONG'
  }
  if (!values.items.length) return 'ZAD.SALES.ITEMS_REQUIRED'
  const products = new Set<string>()
  for (const item of values.items) {
    if (!item.product) return 'ZAD.SALES.PRODUCT_REQUIRED'
    const key = saleLineKey(item.product, item.saleUnit)
    if (products.has(key)) return 'ZAD.SALES.DUPLICATE_PRODUCT'
    products.add(key)
    const qty = parseZadStockQuantity(item.quantity)
    if (!qty.ok) return qty.errorId
    const price = parseZadPriceInput(item.unitPrice, {required: true})
    if (!price.ok) return 'ZAD.SALES.PRICE_REQUIRED'
    if (item.discountType === 'PERCENT') {
      const discount = parseZadPriceInput(item.discountValue, {required: false, defaultValue: 0})
      if (!discount.ok || discount.value > 100) return 'ZAD.SALES.DISCOUNT_INVALID'
    }
    if (item.discountType === 'FIXED') {
      const discount = parseZadPriceInput(item.discountValue, {required: false, defaultValue: 0})
      if (!discount.ok || (price.ok && discount.value > price.value)) {
        return 'ZAD.SALES.DISCOUNT_INVALID'
      }
    }
  }
  if (values.globalDiscountType !== 'NONE') {
    const global = parseZadPriceInput(values.globalDiscountValue, {required: false, defaultValue: 0})
    if (!global.ok) return 'ZAD.SALES.DISCOUNT_INVALID'
    if (values.globalDiscountType === 'PERCENT' && global.value > 100) {
      return 'ZAD.SALES.DISCOUNT_INVALID'
    }
  }
  return null
}

function parsedPrice(value: string | number, required = false): number {
  const parsed = parseZadPriceInput(value, {required, defaultValue: 0})
  return parsed.ok ? parsed.value : 0
}

function parsedQuantity(value: string | number): number {
  const parsed = parseZadStockQuantity(value)
  return parsed.ok ? parsed.value : 0
}

export function toZadSalePayload(values: ZadSaleEditorValues): ZadSaleWritePayload {
  const customer = isZadRegisteredCustomerRef(values.customer) ? String(values.customer).trim() : null
  const walkInName = String(values.walkInCustomerName || '').trim()
  return {
    customer,
    walkInCustomerName: customer ? null : walkInName || null,
    saleDate: String(values.saleDate || '').trim(),
    notes: String(values.notes || '').trim(),
    globalDiscountType: values.globalDiscountType || 'NONE',
    globalDiscountValue:
      values.globalDiscountType === 'NONE' ? 0 : parsedPrice(values.globalDiscountValue),
    promoCodeInput: normalizeZadSalePromoCodeInput(values.promoCodeInput) || null,
    items: values.items.map(
      (item): ZadSaleItemWritePayload => ({
        product: item.product,
        quantity: parsedQuantity(item.quantity),
        unitPrice: parsedPrice(item.unitPrice, true),
        discountType: item.discountType || 'NONE',
        discountValue: item.discountType === 'NONE' ? 0 : parsedPrice(item.discountValue),
        ...(item.packagingEnabled ||
        normalizeZadSaleUnit(item.saleUnit) === 'PACKAGE' ||
        item.unitsPerPackageSnapshot != null
          ? {
              saleUnit: normalizeZadSaleUnit(item.saleUnit),
              ...(item.unitsPerPackageSnapshot != null
                ? {unitsPerPackageSnapshot: item.unitsPerPackageSnapshot}
                : {}),
            }
          : {}),
      })
    ),
  }
}

export function sanitizeZadSaleWritePayload(payload: ZadSaleWritePayload): ZadSaleWritePayload {
  const customer = isZadRegisteredCustomerRef(payload.customer) ? String(payload.customer).trim() : null
  const walkInName = String(payload.walkInCustomerName || '').trim()
  return {
    customer,
    walkInCustomerName: customer ? null : walkInName || null,
    saleDate: String(payload.saleDate || '').trim(),
    notes: String(payload.notes || '').trim(),
    globalDiscountType: payload.globalDiscountType || 'NONE',
    globalDiscountValue:
      payload.globalDiscountType === 'NONE' ? 0 : Number(payload.globalDiscountValue) || 0,
    promoCodeInput: normalizeZadSalePromoCodeInput(payload.promoCodeInput) || null,
    items: (payload.items || []).map((item) => ({
      product: String(item.product || '').trim(),
      quantity: Number(item.quantity) || 0,
      unitPrice: Number(item.unitPrice) || 0,
      discountType: item.discountType || 'NONE',
      discountValue: item.discountType === 'NONE' ? 0 : Number(item.discountValue) || 0,
      ...(item.saleUnit || item.unitsPerPackageSnapshot != null
        ? {
            saleUnit: normalizeZadSaleUnit(item.saleUnit),
            ...(item.unitsPerPackageSnapshot != null
              ? {unitsPerPackageSnapshot: Number(item.unitsPerPackageSnapshot)}
              : {}),
          }
        : {}),
    })),
  }
}

/** Keep submitted walk-in identity if a create/update response omits it, so the next POST cannot wipe Mongo. */
export function mergeSubmittedWalkInIdentity(
  sale: ZadSale,
  submitted: ZadSaleEditorValues
): ZadSale {
  if (getZadSaleCustomerId(sale.customer) || isZadRegisteredCustomerRef(submitted.customer)) {
    return sale
  }
  const submittedName = trimPrintableName(submitted.walkInCustomerName)
  const returnedName = trimPrintableName(sale.walkInCustomerName) || trimPrintableName(sale.customerName)
  const name = returnedName || submittedName || null
  const notes = String(sale.notes || '').trim() || String(submitted.notes || '').trim()
  return {
    ...sale,
    customer: sale.customer ?? null,
    customerKind: sale.customerKind || 'WALK_IN',
    walkInCustomerName: name,
    customerName: trimPrintableName(sale.customerName) || name,
    notes,
  }
}

export function createZadSaleEditorItem(product: ZadProduct, saleUnit?: string | null): ZadSaleEditorItem {
  const unit = defaultZadSaleUnit(product, saleUnit)
  const resolved = resolveZadSaleUnitPrice(product, unit)
  const unitPrice =
    resolved != null ? String(resolved) : product.salePrice == null ? '' : String(product.salePrice)
  const enabled = isZadPackagingEnabled(product)
  const upp = zadUnitsPerPackage(product)
  return {
    key: `${product._id}-${unit}-${Date.now()}`,
    product: product._id,
    productName: product.name || '',
    productCode: product.code || '',
    barcode: preserveZadBarcode(product.barcode),
    quantity: '1',
    unitPrice,
    discountType: 'NONE',
    discountValue: '0',
    saleUnit: unit,
    unitsPerPackageSnapshot: unit === 'PACKAGE' ? upp : enabled ? 1 : undefined,
    packagingLabelSnapshot: enabled ? zadPackagingLabel(product, unit, upp) : null,
    packagingEnabled: enabled,
  }
}

export function buildZadSaleStockMap(items: ZadStockOverviewItem[]): Record<string, number> {
  const map: Record<string, number> = {}
  items.forEach((row) => {
    const id = row.product?._id
    if (id) map[id] = row.currentStock
  })
  return map
}

export function formatZadSaleDiscountLabel(
  type: ZadDiscountType,
  value: number,
  formatMoney: (amount: number) => string
): string {
  if (type === 'PERCENT') return `${value} %`
  if (type === 'FIXED') return `${formatMoney(value)} / unité`
  return '—'
}

export function formatZadSaleCustomerPicker(customer: ZadCustomer): string {
  const name = getZadCustomerDisplayName(customer)
  const number = customer.customerNumber || ''
  const phone = customer.phone || ''
  return [number, name, phone].filter(Boolean).join(' — ')
}

export function formatZadInsufficientStock(err: ZadApiError): string {
  const name = err.details?.productName || err.details?.productCode || ''
  const requested = err.details?.requested
  const available = err.details?.available
  if (name && requested != null && available != null) {
    return `${name}|${requested}|${available}`
  }
  return ''
}

export function resolveZadSaleMessage(err: ZadApiError): {i18nId: string; fallback: string} {
  const map: Record<string, string> = {
    SALE_NOT_FOUND: 'ZAD.SALES.NOT_FOUND',
    SALE_NOT_DRAFT: 'ZAD.SALES.NOT_DRAFT',
    SALE_ALREADY_VALIDATED: 'ZAD.SALES.ALREADY_VALIDATED',
    SALE_CANCELLED: 'ZAD.SALES.CANCELLED',
    CUSTOMER_NOT_FOUND: 'ZAD.CUSTOMERS.NOT_FOUND',
    CUSTOMER_INACTIVE: 'ZAD.SALES.CUSTOMER_INACTIVE',
    PRODUCT_NOT_FOUND: 'ZAD.PURCHASES.PRODUCT_NOT_FOUND',
    PRODUCT_INACTIVE: 'ZAD.STOCK.PRODUCT_INACTIVE',
    PRODUCT_TYPE_INACTIVE: 'ZAD.PRODUCTS.TYPE_INACTIVE',
    CATEGORY_INACTIVE: 'ZAD.PRODUCTS.CATEGORY_INACTIVE',
    DUPLICATE_PRODUCT: 'ZAD.SALES.DUPLICATE_PRODUCT',
    INVALID_QUANTITY: 'ZAD.SALES.INVALID_QUANTITY',
    INVALID_UNIT_PRICE: 'ZAD.SALES.INVALID_PRICE',
    INVALID_DISCOUNT: 'ZAD.SALES.DISCOUNT_INVALID',
    INVALID_GLOBAL_DISCOUNT: 'ZAD.SALES.DISCOUNT_INVALID',
    INSUFFICIENT_STOCK: 'ZAD.SALES.INSUFFICIENT_STOCK',
    VALIDATION_ERROR: 'ZAD.SALES.VALIDATION_ERROR',
    NOT_FOUND: 'ZAD.SALES.NOT_FOUND',
    ROLE_FORBIDDEN: 'ZAD.COMMON.FORBIDDEN',
    SALE_VALIDATION_FORBIDDEN: 'ZAD.SALES.VALIDATION_FORBIDDEN',
    INSUFFICIENT_TENDER: 'ZAD.POS.INSUFFICIENT_TENDER',
    SALE_OVERPAYMENT: 'ZAD.CUSTOMER_PAYMENTS.SALE_OVERPAYMENT',
    COLLECT_OPERATION_CONFLICT: 'ZAD.POS.COLLECT_CONFLICT',
    IDEMPOTENCY_CONFLICT: 'ZAD.POS.COLLECT_CONFLICT',
    WALK_IN_PAYMENT_REQUIRES_SALE: 'ZAD.POS.WALK_IN_INVALID',
    WALK_IN_UNALLOCATED_FORBIDDEN: 'ZAD.POS.WALK_IN_INVALID',
    INVALID_WALK_IN_PAYMENT: 'ZAD.POS.WALK_IN_INVALID',
    CUSTOMER_IDENTITY_REQUIRED: 'ZAD.SALES.PROMO_CODE_CUSTOMER_REQUIRED',
    PROMO_CODE_NOT_FOUND: 'ZAD.SALES.PROMO_CODE_INVALID',
    PROMO_CODE_INVALID: 'ZAD.SALES.PROMO_CODE_INVALID',
    INVALID_PROMO_CODE: 'ZAD.SALES.PROMO_CODE_INVALID',
    PROMO_CODE_INACTIVE: 'ZAD.SALES.PROMO_CODE_INACTIVE',
    PROMO_CODE_SUSPENDED: 'ZAD.SALES.PROMO_CODE_SUSPENDED',
    PROMO_CODE_EXPIRED: 'ZAD.SALES.PROMO_CODE_EXPIRED',
    PROMO_CODE_NOT_STARTED: 'ZAD.SALES.PROMO_CODE_NOT_STARTED',
    PROMO_CODE_UPCOMING: 'ZAD.SALES.PROMO_CODE_NOT_STARTED',
    PROMO_CODE_USAGE_LIMIT: 'ZAD.SALES.PROMO_CODE_USAGE_LIMIT',
    PROMO_CODE_USAGE_LIMIT_REACHED: 'ZAD.SALES.PROMO_CODE_USAGE_LIMIT',
    PROMO_CODE_CUSTOMER_LIMIT: 'ZAD.SALES.PROMO_CODE_CUSTOMER_LIMIT',
    PROMO_CODE_CUSTOMER_LIMIT_REACHED: 'ZAD.SALES.PROMO_CODE_CUSTOMER_LIMIT',
    PROMO_CODE_MINIMUM_AMOUNT: 'ZAD.SALES.PROMO_CODE_MINIMUM',
    PROMO_CODE_MINIMUM_NOT_REACHED: 'ZAD.SALES.PROMO_CODE_MINIMUM',
    PROMO_CODE_NOT_ELIGIBLE: 'ZAD.SALES.PROMO_CODE_NOT_ELIGIBLE',
    PROMO_CODE_TARGET_NOT_ELIGIBLE: 'ZAD.SALES.PROMO_CODE_NOT_ELIGIBLE',
  }
  if (err.errorCode && map[err.errorCode]) {
    return {i18nId: map[err.errorCode], fallback: err.error}
  }
  return resolveZadUserMessage(err)
}
